import {Button, Group, Title, Text, Code, Center, Stack, List} from "@mantine/core";
import {useNavigate} from "react-router";
import {ReactElement, ReactNode} from "react";
import {IconFileUnknown, IconLock, IconMoodSad} from "@tabler/icons-react";
import {useRouteError} from "react-router-dom";
import {ISystemPermissionBase} from "@/types/models/ISystemPermission.ts";

export interface BaseErrorProps {
    icon: ReactElement,
    code?: string | number,
    name: string,
    message: ReactElement,
    otherActions?: ReactNode
    hideReturnToHomepage?: boolean
}

export function BaseError(props: BaseErrorProps) {
    const navigate = useNavigate()
    return <>
        <Center mb={"md"}>{props.icon}</Center>
        {props.code !== undefined && <Title order={2} ta={"center"} m={"xs"}>{props.code}</Title>}
        <Title order={1} ta={"center"} m={"xs"}>{props.name}</Title>
        <Text c="dimmed" size="lg" ta="center" m={"sm"}>
            {props.message}
        </Text>
        <Group justify="center">
            <Group>
                {!props.hideReturnToHomepage && <Button variant="subtle" size="md" onClick={() => {
                    navigate("/")
                }}>
                    Return to the Homepage
                </Button>}
                {props.otherActions}
            </Group>
        </Group>
    </>
}

export interface HttpErrorProps {
    icon: ReactElement,
    code: number,
    name: string,
    message: ReactElement
}

function HttpError(props: HttpErrorProps) {
    return <BaseError {...props}/>
}

export function Error403Page({permissions}: {permissions?: ISystemPermissionBase[]}) {
    const message = <Stack>
        <Text>You are not allowed to view the page at <Code>{window.location.pathname}</Code></Text>
        {permissions && <List title={"Permissions Required"}>
            {permissions.map(perm => <List.Item key={`perm${perm.module}${perm.name}`}>{perm.module}:{perm.name}</List.Item>)}
        </List>}
    </Stack>

    return <HttpError code={403} name={"Forbidden"} message={message} icon={<IconLock size={"4em"}/>}/>
}

export function Error404Page() {
    const message = <>We looked as hard as we could. No page exists at <Code>{window.location.pathname}</Code></>

    return <HttpError code={404} name={"Not Found"} message={message} icon={<IconFileUnknown size={"4em"}/>}/>
}

export function RouterErrorPage() {
    const error = useRouteError()

    //eslint-disable-next-line no-console
    console.error("Router Error", error)

    return <BaseError icon={<IconMoodSad/>} name={"Router Error"} message={<>An unexpected error has been received by the frontend page router. Please check the browser console for more information.</>}/>
}